import NiceModal from '@ebay/nice-modal-react';

import { Suspense, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import Tabs from 'components/global/Tabs';

import useAccessControl from 'hooks/useAccessControl';
import useModalRouter from 'hooks/useModalRouter';

import ImportDataModal from 'modals/ImportDataModal';

import { importActions } from 'pages/Settings/ImportExport/ImportLog/ImportLogList';

import PATHS, { mergePaths } from 'utils/paths';

import { faDownload, faPlus } from '@fortawesome/pro-solid-svg-icons';

import SynchronizeProductModal from './Products/SynchronizeProductsModal';

const Product = () => {
	const { showModal } = useModalRouter();
	const { pathname } = useLocation();
	const isAuthorized = useAccessControl();
	const pimModuleAuthorized = isAuthorized('PIMModule');

	const pathVariables = useMemo(() => {
		if (pathname === mergePaths(PATHS.PRODUCTS_MAIN, PATHS.PRODUCTS)) {
			return ['addProduct', 'product-form', '/modal/product-form'];
		} else if (pathname === mergePaths(PATHS.PRODUCTS_MAIN, PATHS.PRODUCT_ATTRIBUTES)) {
			return ['addProductAttributes', 'product-attribute-form', '/modal/product-attribute-form'];
		} else if (pathname === mergePaths(PATHS.PRODUCTS_MAIN, PATHS.ATTRIBUTES_GROUPS)) {
			return ['addAttributesGroup', 'attributes-group-form', '/modal/attributes-group-form'];
		} else if (pathname === mergePaths(PATHS.PRODUCTS_MAIN, PATHS.PRODUCTS_CATEGORIES)) {
			return ['addCategory', 'category-form', '/modal/category-form'];
		}
	}, [pathname]);

	const tabs = useMemo(
		() => [
			{
				label: 'products',
				path: mergePaths(PATHS.PRODUCTS_MAIN, PATHS.PRODUCTS)
			},
			{
				label: 'categories',
				path: mergePaths(PATHS.PRODUCTS_MAIN, PATHS.PRODUCTS_CATEGORIES)
			},
			...(pimModuleAuthorized
				? [
						{
							label: 'productAttributes',
							path: mergePaths(PATHS.PRODUCTS_MAIN, PATHS.PRODUCT_ATTRIBUTES)
						},
						{
							label: 'attributesGroups',
							path: mergePaths(PATHS.PRODUCTS_MAIN, PATHS.ATTRIBUTES_GROUPS)
						}
					]
				: [])
		],
		[pimModuleAuthorized]
	);

	return (
		<div>
			<PageHeader
				title="products"
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/product-icon.json.gz"
				moduleName="product-information">
				{pathname === mergePaths(PATHS.PRODUCTS_MAIN, PATHS.PRODUCTS) && (
					<Authorization path="/import-export/button/productImport">
						<Button
							className="mobile-hidden importDataButton"
							icon={faDownload}
							variant="borderless"
							label="uploadProduct"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
							onClick={() => NiceModal.show(ImportDataModal, importActions.productImport)}
						/>
						<Button
							className="mobile-hidden importDataButton"
							variant="borderless"
							label="syncProducts"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/sync-product.json.gz"
							onClick={() => NiceModal.show(SynchronizeProductModal)}
						/>
					</Authorization>
				)}
				{pathname === mergePaths(PATHS.PRODUCTS_MAIN, PATHS.PRODUCTS_CATEGORIES) && (
					<Authorization path="/import-export/button/categoryImport">
						<Button
							className="mobile-hidden importDataButton"
							icon={faDownload}
							variant="borderless"
							label="uploadCategory"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
							onClick={() => NiceModal.show(ImportDataModal, importActions.categoryImport)}
						/>
					</Authorization>
				)}
				{pathVariables && (
					<Authorization path={pathVariables[2]}>
						<Button
							icon={faPlus}
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
							label={pathVariables[0]}
							onClick={() => showModal(pathVariables[1], { mode: 'add' })}
							variant="blue"
							className="addButton"
						/>
					</Authorization>
				)}
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default Product;
